import type { KickPlateBulkOrderItem } from 'metalworks-domain'
import { useCallback, useState } from 'react'

import { configuratorInterface } from '../../../systems/configurator-interface'
import { setQuoteId, useMetalworksApi } from '../../../systems/metalworks-api'
import type { FinalizationFormData } from '../shared/JobDetailsForm'

export const useBulkFinalize = () => {
  const { configurationId } = configuratorInterface.useDomain()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const session = configuratorInterface.useSession()
  const sessionPayload = session.sessionPayload

  const $plate = configuratorInterface.useRootEntityState()

  const [returnUrl, setReturnUrl] = useState<string | null>(null)

  const metalworksApi = useMetalworksApi()

  const resolveQuoteId = useCallback(
    async (jobDetailsData: FinalizationFormData) => {
      let quoteId = jobDetailsData.quoteId
      const { finalizationType } = jobDetailsData

      if (finalizationType === 'new') {
        const newQuoteData = await metalworksApi.createQuote({
          data: {
            theirReference: jobDetailsData.theirReference,
          },
        })

        quoteId = newQuoteData.id
        setReturnUrl(newQuoteData.link)
        setQuoteId(configurationId, quoteId)
      }

      if (quoteId === null) {
        throw new Error('Missing quote ID, should not happen.')
      }

      if (finalizationType === 'existingQuote' || finalizationType === 'existingQuoteLine') {
        const existingQuoteData = await metalworksApi.getQuote({ quoteId })

        setReturnUrl(existingQuoteData.link)
      }

      return quoteId
    },
    [configurationId, metalworksApi]
  )

  const finalizeItem = useCallback(
    async (
      quoteId: number,
      jobDetailsData: FinalizationFormData,
      stepFileText: string,
      plateVolume: number,
      item: KickPlateBulkOrderItem
    ) => {
      const selectedCustomMaterial = $plate.$selectedCustomMaterial
      if (!selectedCustomMaterial) {
        throw new Error('Custom material not found. This should happen only in dry-run mode.')
      }

      await apiClient.finalize(configurationId, item.finalizationId, {
        usage: jobDetailsData.usage,
        accessToken: sessionPayload?.data?.accessToken ?? '',
        stepFile: stepFileText,
        quoteId,
        quoteLineId: null,
        materialCode: selectedCustomMaterial.code,
        rawMaterial: selectedCustomMaterial.rawMaterial,
        usedCountersinks: $plate.$usedCountersinks,
        volume: plateVolume,
      })
    },
    [apiClient, configurationId, $plate, sessionPayload]
  )

  const waitForItem = useCallback(
    async (item: KickPlateBulkOrderItem) => {
      await apiClient.waitForFinalizationToComplete(configurationId, item.finalizationId, {
        pollingTimeout: 5 * 60 * 1000, // 5 minutes
        pollingFrequency: 15 * 1000, // 15 seconds
      })
    },
    [apiClient, configurationId]
  )

  return {
    returnUrl,
    resolveQuoteId,
    finalizeItem,
    waitForItem,
  }
}
